<template>
  <div>
    <div class="d-flex align-items-center" style="gap: 2.34rem; margin-bottom: 1.70rem;">
      <div>
        <h1 class="title">{{ $t("risk.rating-definition") }}</h1>
      </div>
    </div>

    <div class="rd-card w-100">
      <div class="d-flex flex-column">
        <div class="d-flex flex-row">
          <div>
            <div class="pswitch">
              {{ $t("risk.applied-risk-value.lowest") }}
            </div>

            <sweet-input
              v-model="form.lowest.min"
              :isValid="!$v.form.lowest.min.$error"
              @input="$v.form.lowest.min.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.min')"
              style="width: 10.625rem;"
              class="mb-3"
            />
            <sweet-input
              v-model="form.lowest.max"
              :isValid="!$v.form.lowest.max.$error"
              @input="$v.form.lowest.max.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.max')"
              style="width: 10.625rem;"
            />
            <div class="d-flex justify-content-end">
              <div class=" mt-2">
                <i class="iconoir-info-circle" v-b-tooltip.hover :title="tooltipText(form.lowest)"></i>
              </div>
            </div>
            <span class="text-danger">{{ form.lowest.error }}</span>
          </div>

          <div class="line"></div>

          <div>
            <div class="pswitch">
              {{ $t("risk.applied-risk-value.low") }}
            </div>

            <sweet-input
              v-model="form.low.min"
              :isValid="!$v.form.low.min.$error"
              @input="$v.form.low.min.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.min')"
              style="width: 10.625rem;"
              class="mb-3"
            />
            <sweet-input
              v-model="form.low.max"
              :isValid="!$v.form.low.max.$error"
              @input="$v.form.low.max.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.max')"
              style="width: 10.625rem;"
            />

            <div class="d-flex justify-content-end">
              <div class=" mt-2">
                <i class="iconoir-info-circle" v-b-tooltip.hover :title="tooltipText(form.low)"></i>
              </div>
            </div>
            <span class="text-danger">{{ form.low.error }}</span>
          </div>

          <div class="line"></div>

          <div>
            <div class="pswitch">
              {{ $t("risk.applied-risk-value.medium") }}
            </div>

            <sweet-input
              v-model="form.medium.min"
              :isValid="!$v.form.medium.min.$error"
              @input="$v.form.medium.min.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.min')"
              style="width: 10.625rem;"
              class="mb-3"
            />
            <sweet-input
              v-model="form.medium.max"
              :isValid="!$v.form.medium.max.$error"
              @input="$v.form.medium.max.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.max')"
              style="width: 10.625rem;"
            />

            <div class="d-flex justify-content-end">
              <div class=" mt-2">
                <i class="iconoir-info-circle" v-b-tooltip.hover :title="tooltipText(form.medium)"></i>
              </div>
            </div>
            <span class="text-danger">{{ form.medium.error }}</span>
          </div>

          <div class="line"></div>

          <div>
            <div class="pswitch">
              {{ $t("risk.applied-risk-value.high") }}
            </div>

            <sweet-input
              v-model="form.high.min"
              :isValid="!$v.form.high.min.$error"
              @input="$v.form.high.min.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.min')"
              style="width: 10.625rem;"
              class="mb-3"
            />
            <sweet-input
              v-model="form.high.max"
              :isValid="!$v.form.high.max.$error"
              @input="$v.form.high.max.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.max')"
              style="width: 10.625rem;"
            />

            <div class="d-flex justify-content-end">
              <div class=" mt-2">
                <i class="iconoir-info-circle" v-b-tooltip.hover :title="tooltipText(form.high)"></i>
              </div>
            </div>
            <span class="text-danger">{{ form.high.error }}</span>
          </div>

          <div class="line"></div>

          <div>
            <div class="pswitch">
              {{ $t("risk.applied-risk-value.highest") }}
            </div>

            <sweet-input
              v-model="form.highest.min"
              :isValid="!$v.form.highest.min.$error"
              @input="$v.form.highest.min.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-value.min')"
              style="width: 10.625rem;"
              class="mb-3"
            />
            <sweet-input
              v-model="form.highest.max"
              :isValid="!$v.form.highest.max.$error"
              @input="$v.form.highest.max.$touch()"
              layout="vertical"
              type="number"
              :label="$t('risk.applied-risk-decision.max')"
              style="width: 10.625rem;"
            />

            <div class="d-flex justify-content-end">
              <div class=" mt-2">
                <i class="iconoir-info-circle" v-b-tooltip.hover :title="tooltipText(form.highest)"></i>
              </div>
            </div>
            <span class="text-danger">{{ form.highest.error }}</span>
          </div>
        </div>
        <div class="d-flex flex-row">
          <div class="table-responsive w-30 mt-4">
            <table class="table table-bordered table-striped">
              <thead>
                <tr>
                  <th rowspan="2" class="align-middle text-center bg-warning">{{ $t("risk.degree.title") }}</th>
                  <th colspan="5" class="text-center bg-warning">{{ $t("risk.effect") }}</th>
                </tr>
                <tr>
                  <th v-for="effect in effects" :key="effect" class="text-center bg-warning">{{ effect }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(criticality, cIndex) in criticalities" :key="cIndex">
                  <td class="bg-warning text-center align-middle">{{ criticality }}</td>
                  <td style="width:60px" v-for="effect in effects" :key="effect">
                    <div :class="getCellColor(criticality, effect)" class="text-center" style="padding:0">
                      {{ criticality * effect * effect }}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colspan="1" class="align-middle text-center bg-warning">
                    <strong> {{ $t("risk.possibility") }}</strong>
                  </td>
                  <td v-for="possibility in possibilities" :key="possibility" class="bg-warning text-center align-middle">{{ possibility }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pswitch mt ml-4 align-items-center">
            <div class="legend">
              <div class="legend-item">
                <div class="box bg-danger"></div>
                <span>{{ $t("risk.degree.prevent") }}</span>
              </div>
              <div class="legend-item">
                <div class="box bg-light-warning"></div>
                <span>{{ $t("risk.degree.recommend-prevent") }}</span>
              </div>
              <div class="legend-item">
                <div class="box bg-success"></div>
                <span>{{ $t("risk.degree.accept") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="rd-card w-100">
      <div class="d-flex mt-4 mt-auto ml-auto w-100" style="max-width: 24rem; gap: 0.75rem;">
        <button @click="save" :disabled="error || $v.form.$invalid" class="button save-button w-50 flex-shrink-1">
          {{ $t("common.save") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { numeric, required } from "vuelidate/lib/validators";
export default {
  data() {
    return {
      criticalities: [1, 2, 3],
      effects: [1, 2, 3, 4, 5],
      possibilities: [1, 2, 3, 4, 5],
      error: false,
      form: {
        lowest: {
          min: undefined,
          max: undefined,
        },
        low: {
          min: undefined,
          max: undefined,
        },
        medium: {
          min: undefined,
          max: undefined,
        },
        high: {
          min: undefined,
          max: undefined,
        },
        highest: {
          min: undefined,
          max: undefined,
        },
      },
    };
  },
  validations: {
    form: {
      lowest: {
        min: {
          numeric,
          required,
          maxValue: (value, lowest) => {
            return !lowest.max || Number(value) < Number(lowest.max);
          },
        },
        max: {
          numeric,
          required,
        },
      },
      low: {
        min: {
          numeric,
          required,
          maxValue: (value, low) => {
            return !low.max || Number(value) < Number(low.max);
          },
        },
        max: {
          numeric,
          required,
        },
      },
      medium: {
        min: {
          numeric,
          required,
          maxValue: (value, medium) => {
            return !medium.max || Number(value) < Number(medium.max);
          },
        },
        max: {
          numeric,
          required,
        },
      },
      high: {
        min: {
          numeric,
          required,
          maxValue: (value, high) => {
            return !high.max || Number(value) < Number(high.max);
          },
        },
        max: {
          numeric,
          required,
        },
      },

      highest: {
        min: {
          numeric,
          required,
          maxValue: (value, highest) => {
            return !highest.max || Number(value) < Number(highest.max);
          },
        },
        max: {
          numeric,
          required,
        },
      },
    },
  },
  computed: {
    editedRatingDefinition() {
      return this.$store.getters[`organizationRisk/editedRatingDefinition`];
    },

    riskRatingDefinition() {
      return this.$store.getters[`organizationRisk/riskRatingDefinition`];
    },
  },
  watch: {
    form: {
      deep: true,
      handler(val) {
        this.validate(val);
      },
    },
    editedRatingDefinition(v) {
      this.form = JSON.parse(JSON.stringify(this.prepareFormForEdit(v)));
    },
  },
  async mounted() {
    await this.fetchRiskRatingDefinition();
  },
  methods: {
    getCellColor(criticality, impact) {
      const value = criticality * impact * impact;
      if (value < this.form.medium.min) {
        return "bg-success";
      } else if (value < this.form.high.min) {
        return "bg-light-warning";
      } else {
        return "bg-danger";
      }
    },
    tooltipText(value) {
      return `${this.$t("risk.applied-risk-value.min-value")} ${value.min}'${this.$t("risk.applied-risk-value.bigger")} ${value.max}${this.$t("risk.applied-risk-value.smaller")}`;
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`organizationRisk/saveRiskRatingDefinition`, this.form);
        await this.fetchRiskRatingDefinition();

        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async fetchRiskRatingDefinition() {
      const data = await this.$store.dispatch(`organizationRisk/fetchRiskRatingDefinition`);
      this.$store.commit("organizationRisk/editedRatingDefinition", { ...data });
    },

    onSelect() {
      this.$forceUpdate();
    },
    validate() {
      this.error = false;

      const keys = ["lowest", "low", "medium", "high", "highest"];
      const keyMap = {
        lowest: this.$t("risk.applied-risk-value.lowest"),
        low: this.$t("risk.applied-risk-value.low"),
        medium: this.$t("risk.applied-risk-value.medium"),
        high: this.$t("risk.applied-risk-value.high"),
        highest: this.$t("risk.applied-risk-value.highest"),
      };
      keys.forEach((i) => {
        this.form[i].error = undefined;
      });

      keys.forEach((i) => {
        keys.forEach((j) => {
          const ides = this.form[i];
          const jdes = this.form[j];
          if (i == j) {
            return;
          }

          if (Number(ides.max) > Number(jdes.min) && Number(ides.min) < Number(jdes.max)) {
            ides.error = `${this.$t(keyMap[j])} ${this.$t("risk.applied-risk-value.intersects-with-it")}`;
            jdes.error = `${this.$t(keyMap[i])} ${this.$t("risk.applied-risk-value.intersects-with-it")}`;

            this.error = true;
          }
        });
      });
      this.$forceUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./organization-risk-decision.scss";

.legend {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.bg-light-warning {
  background-color: #ffbd8a;
}
.box {
  width: 20px;
  height: 20px;
}
</style>
